<script lang="ts" setup>
import { DefaultButton } from '@hantecmarket/hantec-ui-widget'
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'

import Icon from '@/components/Icons/Icon.vue'
import LanguageSwitcher from '@/components/LanguageSwitcher.vue'
import { __, resolveRouteName } from '@/utils/utils'
import { navigateToLink } from '@/utils/nagivationService'
import { getEnv } from '@/utils/environmentService'

const props = defineProps({
  menusData: Array<any>
})

const activeMenu = ref<string | null>(null)

const siteUrl = `${document.location.origin}/`

let lang: string = ''

const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null
  return null
}

const country = computed(() => {
  return getCookie('country') || ''
})

onMounted(() => {
  lang =
    localStorage.getItem('lang') !== '' &&
    localStorage.getItem('lang') !== 'undefined' &&
    localStorage.getItem('lang') !== 'en'
      ? localStorage.getItem('lang') + '/'
      : ''

  window.addEventListener('change-active-menu', (event: any) => {
    openMenu(event.detail, event)
  })
  window.addEventListener('mouse-out-menu', () => {
    closeMenu()
  })
})

const openMenu = (menu: any, event: Event) => {
  if (event && event.stopPropagation) {
    event.stopPropagation()
  }
  if (event === undefined) {
    return
  }
  activeMenu.value = menu
}

const closeMenu = () => {
  activeMenu.value = null
  const eventActive = new CustomEvent('close-menu')
  window.dispatchEvent(eventActive)
}

const allSubMenus = computed(() => {
  return (
    props.menusData?.flatMap((menu) =>
      (menu.child_items || []).map((childItem: any) => ({
        ...childItem,
        menuLabel: menu.label,
        parentUrl: menu.url
      }))
    ) || []
  )
})

const activeSubMenus = computed(() => {
  const menus = allSubMenus.value.map((subMenu) => ({
    ...subMenu,
    visible: subMenu.menuLabel === activeMenu.value
  }))
  return menus || {}
})

const activeQuickLinks = computed(() => {
  const menu = props?.menusData?.find((menu) => menu.label === activeMenu.value)
  return menu ? menu.quicklinksitems : []
})

const activeMenuDescription = computed(() => {
  const menu = props?.menusData?.find((menu) => menu.label === activeMenu.value)
  return menu ? menu.description : ''
})

const activeMenuIcon = computed(() => {
  const menu = props?.menusData?.find((menu) => menu.label === activeMenu.value)
  return menu ? menu.icon : ''
})

const openedLanguageMenu = ref(false)

const handleToggleSwitcher = () => {
  openedLanguageMenu.value = !openedLanguageMenu.value
}

const getSubMenuHref = (subMenu: any) => {
  if (subMenu?.url?.startsWith('http://') || subMenu?.url?.startsWith('https://')) {
    return subMenu.url
  }
  return siteUrl + lang + subMenu.parentUrl + '/' + `${subMenu?.url ? `${subMenu?.url}/` : ''}`
}

const getSubMenuTarget = (subMenu: any) => {
  return subMenu?.url?.startsWith('http') ? '_blank' : '_self'
}

const getSubMenuRel = (subMenu: any) => {
  return subMenu?.url?.startsWith('http') ? 'noopener noreferrer' : undefined
}

const isExternalUrl = (url: string | undefined): boolean => {
  return !!(url?.startsWith('http://') || url?.startsWith('https://'))
}

const route = useRoute()
const routeLang = computed(() => route.params.lang || '')
const partnersLang = computed(() =>
  routeLang.value === 'pt' ? 'pt-pt' : (routeLang.value === 'ko' || routeLang.value === 'zh-hans' || routeLang.value === 'zh-hant' || routeLang.value === 'jp') ? '' : routeLang.value
)
const propTradingLang = computed(() =>
  routeLang.value === 'pt' ? 'pt-pt' : (routeLang.value === 'zh-hans' || routeLang.value === 'zh-hant' || routeLang.value === 'jp') ? '' : routeLang.value
)
const { VITE_PARTNERS_URL } = import.meta.env
const loginUrl = getEnv() !== 'hk' ? ('https://portal.hmarkets.' + getEnv() + '/') : 'https://portal.hmglobal.hk/'
</script>

<template>
  <div
    id="mega-menu"
    class="relative w-full h-full bg-white 2lg:flex 2lg:flex-row 2lg:justify-between 2lg:border-t-2 2lg:border-black"
    @mouseleave="closeMenu"
  >
    <!-- Top Level Tabs -->
    <div class="flex justify-around bg-black border-b 2lg:hidden">
      <div
        class="pt-8.75 pb-5 border-b-2 border-hantec-primary cursor-pointer text-hantec-primary text-sm font-bold"
      >
        <a :href="`${siteUrl}${lang}/`.toValidUrl()">{{ __('Retail') }}</a>
      </div>
      <div class="pt-8.75 text-white pb-5 cursor-pointer text-hantec-primary text-sm font-bold">
        <a href="https://hantecprime.com/">{{ __('Institutional') }}</a>
      </div>

      <!-- <div class="pt-8.75 pb-5 text-white cursor-pointer text-hantec-primary text-sm font-bold cursor-pointer">
        <span @click="navigateToLink(`https://htrader.hmarkets.com/${propTradingLang}`)">{{ __('Prop Trading') }}</span>
      </div> -->
      <div
        v-if="country !== 'KR' && routeLang !== 'ko' && country !== 'BR'"
        class="pt-8.75 pb-5 text-white cursor-pointer text-hantec-primary text-sm font-bold"
      >
        <a :href="`${VITE_PARTNERS_URL}${partnersLang}`">{{ __('Partners') }}</a>
      </div>
    </div>

    <!-- Main Menus -->
    <div v-if="activeMenu === null" class="py-8 pl-5 2lg:hidden">
      <ul class="grid grid-cols-2 gap-y-7.5">
        <li
          v-for="menu in menusData"
          :key="menu?.meta?.id"
          class="flex items-center text-lg font-normal leading-normal"
        >
          <a
            v-if="isExternalUrl(menu?.url)"
            :href="menu.url"
            target="_blank"
            rel="noopener noreferrer"
            class="flex items-center"
          >
            <img :src="menu?.icon" class="mr-1.25 w-5 h-5" loading="lazy" />
            <span class="mr-1.25">{{ menu?.label }}</span>
          </a>
          <span
            v-else
            class="flex items-center cursor-pointer"
            @click.prevent.stop="openMenu(menu?.label, $event)"
          >
            <img :src="menu?.icon" class="mr-1.25 w-5 h-5" loading="lazy" />
            <span class="mr-1.25">{{ menu?.label }}</span>
            <span>&gt;</span>
          </span>
        </li>
      </ul>
    </div>

    <!--Title and description-->
    <div v-if="activeMenu !== null" class="hidden 2lg:block 2lg:pl-10 1440:pl-30 pt-9">
      <div class="flex flex-row mb-2.5">
        <div class="flex items-center">
          <img :src="activeMenuIcon" class="mr-2 w-8.75 h-8.75" loading="lazy" />
        </div>
        <div class="h2-regular">{{ activeMenu }}</div>
      </div>
      <div class="mb-10 text-base font-normal leading-normal text-gray-500 max-w-60">
        {{ activeMenuDescription }}
      </div>
    </div>

    <!-- Sub Menus -->
    <div
      class="px-5 py-10 2lg:px-10 1440:px-5 md:py-10"
      :class="[activeMenu !== null ? 'block' : 'hidden']"
    >
      <div
        class="flex items-center justify-between cursor-pointer 2lg:hidden w-fit"
        @click="closeMenu"
      >
        <span class="text-lg font-bold leading-normal mb-7.5">&lt; {{ activeMenu }}</span>
      </div>
      <ul class="grid grid-cols-2 gap-y-[5px] gap-x-[10px] 2lg:px-0 1440:px-10 md:gap-10">
        <li
          v-for="subMenu in activeSubMenus"
          :key="subMenu?.meta?.id"
          class="overflow-hidden text-base font-normal leading-normal text-black cursor-pointer md:text-lg"
          :class="{ hidden: !subMenu.visible, block: subMenu.visible }"
        >
          <a
            v-if="subMenu?.parentUrl"
            :href="getSubMenuHref(subMenu)"
            :target="getSubMenuTarget(subMenu)"
            :rel="getSubMenuRel(subMenu)"
          >
            <div>{{ subMenu?.menu_item_label }}</div>
            <div
              class="hidden text-base font-normal leading-normal text-gray-500 whitespace-nowrap"
            >
              {{ subMenu?.description }}
            </div>
          </a>
        </li>
      </ul>
    </div>

    <!--Footer mega menu-->
    <div class="bg-gray-50">
      <div
        v-if="activeMenu !== null"
        class="hidden px-5 pt-5 2lg:px-10 1440:px-30 md:pt-10 2lg:block"
      >
        <div class="text-lg font-normal leading-normal text-gray-850 mb-5 md:text-black md:mb-4.5">
          {{ __('Quick Links') }}
        </div>
        <ul class="grid grid-cols-2 gap-y-5 md:flex md:flex-col">
          <li
            v-for="link in activeQuickLinks"
            :key="link?.meta?.id"
            class="text-sm font-normal leading-normal cursor-pointer md:text-base text-gray-850 md:text-black"
          >
            <a :href="siteUrl + lang + link?.quicklinkurl + '/'">
              {{ link.quicklinklabel }}
            </a>
          </li>
        </ul>
      </div>
      <div v-if="activeMenu === null" class="pt-5 pl-5 2lg:hidden">
        <div class="mb-5 text-lg font-normal leading-normal text-gray-850">
          {{ __('Featured Products') }}
        </div>
        <ul class="grid grid-cols-2">
          <li class="flex flex-row">
            <Icon name="shield-red"></Icon>
            <div class="ml-1 text-base font-normal leading-6 text-gray-850">
              <a :href="resolveRouteName('balanceguard')">{{ __('Balance Guard') }}</a>
            </div>
          </li>
          <li class="flex flex-row">
            <Icon name="hantec-logo"></Icon>
            <div class="ml-1 text-base font-normal leading-6 text-gray-850">
              <a :href="resolveRouteName('platformSocial')">{{ __('Hantec Social') }}</a>
            </div>
          </li>
        </ul>
      </div>

      <DefaultButton
        :href="siteUrl + lang + 'live-account-pre-registration' + '/'"
        class="text-white flex px-8 py-4 sm:flex items-center uppercase text-base rounded-1.5 leading-normal font-bold h-12.5 mt-10 mx-5 justify-center 2lg:hidden"
      >
        <div>{{ __('Open an Account') }}</div>
      </DefaultButton>
      <div class="flex px-10 py-6 shadow-md justify-evenly 2lg:hidden">
        <div class="text-sm font-bold leading-normal">
          <a :href="`${loginUrl}${lang === '' ? 'en/' : lang === 'zh-hans/' ? 'cns/' : lang === 'zh-hant/' ? 'cnt/' : lang}login`">
            {{ __('Login') }}
          </a>
        </div>
        <div class="w-px h-5 mx-2 bg-gray-300"></div>
        <div class="text-sm font-bold leading-normal">
          <a :href="siteUrl + lang + 'company/contact-us/'">{{ __('Contact Us') }}</a>
        </div>
        <div class="w-px h-5 mx-2 bg-gray-300"></div>
        <LanguageSwitcher
          :isMegaMenu="true"
          @toggleSwitcher="handleToggleSwitcher"
        ></LanguageSwitcher>
      </div>
    </div>
  </div>
</template>
